import * as Sentry from '@sentry/browser';
import {isEqual} from 'lodash';
import {useEffect, useLayoutEffect, useReducer, useRef} from 'react';
import {unstable_batchedUpdates} from 'react-dom';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import environment from '../../../../RelayEnvironment';
import StartAgenticWorkerRunMutation from '../../../../mutations/agentic-workers/StartAgenticWorkerRunMutation';
import StopAgenticWorkerRunMutation from '../../../../mutations/agentic-workers/StopAgenticWorkerRunMutation';
import type {
	AgenticWorkerRowType,
	StartAgenticWorkerRunMutation$data,
} from '../../../../mutations/agentic-workers/__generated__/StartAgenticWorkerRunMutation.graphql';
import type {StopAgenticWorkerRunMutation$data} from '../../../../mutations/agentic-workers/__generated__/StopAgenticWorkerRunMutation.graphql';
import Util from '../../util/util';
import {createToast} from '../toasts/toast';
import type {agentRunStateQuery} from './__generated__/agentRunStateQuery.graphql';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import {
	AGENT_RUN_SCOPE,
	AGENT_RUN_STATE,
	type AgentDeclaration,
	type AgentResult,
	type AgentResultState,
	type AgentRow,
	type AgentRun,
	type AgentRunScope,
	type AgentRunState,
} from './agentDeclaration';

export const POLL_INTERVAL_MILLISECONDS = 3000;
const RUN_STOPPED_TOAST_DURATION_MILLISECONDS = 5000;

export const PAGE_RUN_SCOPE: AgentRunScope = {kind: AGENT_RUN_SCOPE.PAGE};

export const AGENT_ROW_STATUS = {
	IDLE: 'IDLE',
	WORKING: 'WORKING',
	READY: 'READY',
} as const;
export type AgentRowStatus = (typeof AGENT_ROW_STATUS)[keyof typeof AGENT_ROW_STATUS];

export type AgentRowState = {
	status: AgentRowStatus;
	result: AgentResult | null;
};

export type AgentRunSnapshot = {
	activeRuns: AgentRun[];
	// Newest run first.
	waitingResults: AgentResult[];
	pendingStartKeys: ReadonlySet<string>;
	heldStopKeys: ReadonlySet<string>;
	stoppingRunIds: ReadonlySet<string>;
	restartAfterStopKeys: ReadonlySet<string>;
};

type AgentRunListener = () => void;

type StopListener = () => void;

const STOP_ANSWER = {
	STOPPED: 'STOPPED',
	FINISHED_FIRST: 'FINISHED_FIRST',
	FAILED: 'FAILED',
} as const;
type StopAnswer = (typeof STOP_ANSWER)[keyof typeof STOP_ANSWER];

type AgentRunEntry = {
	snapshot: AgentRunSnapshot;
	listeners: Set<AgentRunListener>;
	pendingStarts: Set<string>;
	// By scope key: a Stop clicked before its start answered.
	heldStops: Map<string, StopListener>;
	// By run id: a stop sent and not yet answered.
	stopsInFlight: Map<string, StopListener>;
	// By scope key: a Run clicked while that scope's stop was held or in flight, sent once a sent stop settles.
	restartsAfterStop: Map<string, AgentRunScope>;
	isReading: boolean;
	// Set when the store asks for a read because its snapshot is not read yet or out of date, so polling carries on
	// until a read succeeds.
	needsFreshRead: boolean;
	// Polling retries a failing read every few seconds, so a run of failures is reported once.
	hasReportedFailedRead: boolean;
	pollTimer: ReturnType<typeof setTimeout> | null;
	// A read sent before the latest answered start or stop can be stale, so its answer is thrown away.
	answeredChanges: number;
};

type RunNode = {
	readonly id: string;
	readonly scopeKind: string;
	readonly scopeRowType?: string | null;
	readonly scopeRowId?: number | null;
	readonly state: string;
};

type ResultNode = {
	readonly id: string;
	readonly runId: string;
	readonly rows: ReadonlyArray<{
		readonly rowType: string;
		readonly rowId: number;
	}>;
	readonly state: string;
	readonly hidden: boolean;
};

const query = graphql`
	query agentRunStateQuery($agentId: String!) {
		viewer {
			agenticWorker(agentId: $agentId) {
				activeRuns {
					id
					scopeKind
					scopeRowType
					scopeRowId
					state
				}
				waitingResults {
					id
					runId
					rows {
						rowType
						rowId
					}
					state
					hidden
				}
			}
		}
	}
`;

const EMPTY_SNAPSHOT: AgentRunSnapshot = {
	activeRuns: [],
	waitingResults: [],
	pendingStartKeys: new Set(),
	heldStopKeys: new Set(),
	stoppingRunIds: new Set(),
	restartAfterStopKeys: new Set(),
};

const entries = new Map<string, AgentRunEntry>();

const entryFor = (agentId: string): AgentRunEntry => {
	const existing = entries.get(agentId);
	if (existing) {
		return existing;
	}
	const created: AgentRunEntry = {
		snapshot: EMPTY_SNAPSHOT,
		listeners: new Set(),
		pendingStarts: new Set(),
		heldStops: new Map(),
		stopsInFlight: new Map(),
		restartsAfterStop: new Map(),
		isReading: false,
		needsFreshRead: false,
		hasReportedFailedRead: false,
		pollTimer: null,
		answeredChanges: 0,
	};
	entries.set(agentId, created);
	return created;
};

const toRowScope = (row: AgentRow): AgentRunScope => ({kind: AGENT_RUN_SCOPE.ROW, ...row});

export const toScopeKey = (scope: AgentRunScope): string =>
	scope.kind === AGENT_RUN_SCOPE.PAGE ? AGENT_RUN_SCOPE.PAGE : `${AGENT_RUN_SCOPE.ROW}#${scope.rowType}#${scope.rowId}`;

// A row run that lost its row would read as a page run, so it is dropped.
const toAgentRun = (node: RunNode): AgentRun | null => {
	const state = node.state as AgentRunState;
	if (node.scopeKind !== AGENT_RUN_SCOPE.ROW) {
		return {
			id: node.id,
			scope: PAGE_RUN_SCOPE,
			state,
		};
	}
	if (!node.scopeRowType || typeof node.scopeRowId !== 'number') {
		return null;
	}
	return {
		id: node.id,
		scope: {
			kind: AGENT_RUN_SCOPE.ROW,
			rowType: node.scopeRowType,
			rowId: node.scopeRowId,
		},
		state,
	};
};

const isAgentRun = (run: AgentRun | null): run is AgentRun => run !== null;

const toAgentResult = (node: ResultNode): AgentResult => ({
	id: node.id,
	runId: node.runId,
	rows: node.rows.map(row => ({
		rowType: row.rowType,
		rowId: row.rowId,
	})),
	state: node.state as AgentResultState,
	hidden: node.hidden,
});

const toScopeInput = (scope: AgentRunScope) =>
	scope.kind === AGENT_RUN_SCOPE.PAGE
		? {scopeKind: AGENT_RUN_SCOPE.PAGE}
		: {
				scopeKind: AGENT_RUN_SCOPE.ROW,
				// Host pages name their row types; the records accept the values the server's enum lists.
				scopeRowType: scope.rowType as AgenticWorkerRowType,
				scopeRowId: scope.rowId,
		  };

const needsPolling = (entry: AgentRunEntry): boolean =>
	entry.needsFreshRead ||
	entry.snapshot.activeRuns.length > 0 ||
	entry.pendingStarts.size > 0 ||
	entry.stopsInFlight.size > 0;

const cancelPoll = (entry: AgentRunEntry): void => {
	if (entry.pollTimer !== null) {
		clearTimeout(entry.pollTimer);
		entry.pollTimer = null;
	}
};

const schedulePoll = (agentId: string, entry: AgentRunEntry): void => {
	if (entry.pollTimer !== null || entry.isReading || entry.listeners.size === 0 || !needsPolling(entry)) {
		return;
	}
	entry.pollTimer = setTimeout(() => {
		entry.pollTimer = null;
		// schedulePoll and readAgentRuns call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		readAgentRuns(agentId);
	}, POLL_INTERVAL_MILLISECONDS);
};

const publish = (
	agentId: string,
	entry: AgentRunEntry,
	changes: Partial<Pick<AgentRunSnapshot, 'activeRuns' | 'waitingResults'>> = {}
): void => {
	entry.snapshot = {
		...entry.snapshot,
		...changes,
		pendingStartKeys: new Set(entry.pendingStarts),
		heldStopKeys: new Set(entry.heldStops.keys()),
		stoppingRunIds: new Set(entry.stopsInFlight.keys()),
		restartAfterStopKeys: new Set(entry.restartsAfterStop.keys()),
	};
	// Outside a React event, React 16 renders each listener's update on its own, so they are batched into one render.
	unstable_batchedUpdates(() => {
		entry.listeners.forEach(listener => listener());
	});
	schedulePoll(agentId, entry);
};

const applyRead = (agentId: string, entry: AgentRunEntry, activeRuns: AgentRun[], waitingResults: AgentResult[]): void => {
	if (isEqual(entry.snapshot.activeRuns, activeRuns) && isEqual(entry.snapshot.waitingResults, waitingResults)) {
		return;
	}
	publish(agentId, entry, {activeRuns, waitingResults});
};

function readAgentRuns(agentId: string): void {
	const entry = entryFor(agentId);
	if (entry.isReading) {
		return;
	}
	cancelPoll(entry);
	entry.isReading = true;
	const changesWhenSent = entry.answeredChanges;
	// A module-level store shared by many controls cannot use the useForecastFetchQuery hook, and this read is
	// supplementary, not the page's loading query.
	fetchQuery<agentRunStateQuery>(environment.getInstance(), query, {agentId})
		.toPromise()
		.then(
			response => {
				if (entries.get(agentId) !== entry) {
					return;
				}
				entry.isReading = false;
				entry.hasReportedFailedRead = false;
				if (entry.answeredChanges !== changesWhenSent) {
					entry.needsFreshRead = true;
					if (entry.listeners.size > 0) {
						readAgentRuns(agentId);
					}
					return;
				}
				entry.needsFreshRead = false;
				// The server answers no worker while the feature is off for this viewer, so nothing runs or waits.
				const worker = response?.viewer?.agenticWorker;
				applyRead(
					agentId,
					entry,
					worker?.activeRuns.map(toAgentRun).filter(isAgentRun) ?? [],
					worker?.waitingResults.map(toAgentResult) ?? []
				);
				schedulePoll(agentId, entry);
			},
			() => {
				if (entries.get(agentId) !== entry) {
					return;
				}
				entry.isReading = false;
				if (!entry.hasReportedFailedRead) {
					entry.hasReportedFailedRead = true;
					Sentry.captureMessage(
						`Reading the runs of agent "${agentId}" failed; keeping the last known runs`,
						'warning'
					);
				}
				schedulePoll(agentId, entry);
			}
		);
}

const requestFreshRead = (agentId: string, entry: AgentRunEntry): void => {
	entry.needsFreshRead = true;
	readAgentRuns(agentId);
};

const findActiveRun = (entry: AgentRunEntry, scopeKey: string): AgentRun | undefined =>
	entry.snapshot.activeRuns.find(run => toScopeKey(run.scope) === scopeKey);

const isStopUnderway = (entry: AgentRunEntry, scopeKey: string): boolean => {
	if (entry.pendingStarts.has(scopeKey)) {
		return entry.heldStops.has(scopeKey);
	}
	const run = findActiveRun(entry, scopeKey);
	return run !== undefined && entry.stopsInFlight.has(run.id);
};

type StandardErrorFlow = (error: Error) => boolean | void;

const finishStop = (agentId: string, run: AgentRun, answer: StopAnswer): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(run.scope);
	const onStopped = entry.stopsInFlight.get(run.id);
	entry.stopsInFlight.delete(run.id);
	if (answer === STOP_ANSWER.FAILED) {
		// The person has just seen the standard error modal, and starting again would most likely show a second one.
		entry.restartsAfterStop.delete(scopeKey);
	}
	entry.answeredChanges += 1;
	publish(agentId, entry, {activeRuns: entry.snapshot.activeRuns.filter(active => active.id !== run.id)});
	if (answer !== STOP_ANSWER.STOPPED) {
		requestFreshRead(agentId, entry);
	}
	const restart = entry.restartsAfterStop.get(scopeKey);
	if (restart) {
		// Removed only now, so the control reads Working throughout instead of flickering idle.
		entry.restartsAfterStop.delete(scopeKey);
		// A start can hold a stop, and a stop can hold a start, so the two call each other.
		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		startAgentRun(agentId, restart);
	}
	const isStartingAgain = entry.pendingStarts.has(scopeKey) || entry.restartsAfterStop.has(scopeKey);
	if (restart && !isStartingAgain) {
		// The start found another run going for the scope and sent nothing, so the waiting Run leaves the snapshot here.
		publish(agentId, entry);
	}
	// While the scope starts again at once, "Run stopped" would contradict the person's last click.
	if (answer === STOP_ANSWER.STOPPED && !isStartingAgain) {
		onStopped?.();
	}
};

const sendStop = (agentId: string, run: AgentRun, onStopped: StopListener): void => {
	const entry = entryFor(agentId);
	entry.stopsInFlight.set(run.id, onStopped);
	publish(agentId, entry);
	Util.CommitMutation(
		StopAgenticWorkerRunMutation,
		{id: run.id},
		(response: StopAgenticWorkerRunMutation$data) => {
			finishStop(
				agentId,
				run,
				response?.stopAgenticWorkerRun?.run?.state === AGENT_RUN_STATE.STOPPED
					? STOP_ANSWER.STOPPED
					: STOP_ANSWER.FINISHED_FIRST
			);
		},
		true,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finishStop(agentId, run, STOP_ANSWER.FAILED);
		}
	);
};

const finishStart = (agentId: string, scopeKey: string, run: AgentRun | null): void => {
	const entry = entryFor(agentId);
	const heldStop = entry.heldStops.get(scopeKey);
	entry.pendingStarts.delete(scopeKey);
	entry.heldStops.delete(scopeKey);
	entry.answeredChanges += 1;
	if (run && run.state === AGENT_RUN_STATE.RUNNING) {
		publish(agentId, entry, {activeRuns: [...entry.snapshot.activeRuns.filter(active => active.id !== run.id), run]});
		if (heldStop) {
			sendStop(agentId, run, heldStop);
		}
		return;
	}
	// A held Stop that is never sent drops the Run waiting on it: after NOT_STARTED a run is already going for the
	// scope, and after a failure sending it again would only fail again.
	entry.restartsAfterStop.delete(scopeKey);
	publish(agentId, entry);
	if (run && run.state === AGENT_RUN_STATE.NOT_STARTED) {
		requestFreshRead(agentId, entry);
	}
};

export const getAgentRunSnapshot = (agentId: string): AgentRunSnapshot => entries.get(agentId)?.snapshot ?? EMPTY_SNAPSHOT;

export const subscribeToAgentRun = (agentId: string, listener: AgentRunListener): (() => void) => {
	const entry = entryFor(agentId);
	const isFirstListener = entry.listeners.size === 0;
	entry.listeners.add(listener);
	if (isFirstListener) {
		requestFreshRead(agentId, entry);
	}
	return () => {
		entry.listeners.delete(listener);
		if (entry.listeners.size === 0) {
			cancelPoll(entry);
		}
	};
};

export const startAgentRun = (agentId: string, scope: AgentRunScope): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(scope);
	if (isStopUnderway(entry, scopeKey)) {
		if (!entry.restartsAfterStop.has(scopeKey)) {
			entry.restartsAfterStop.set(scopeKey, scope);
			publish(agentId, entry);
		}
		return;
	}
	if (entry.pendingStarts.has(scopeKey) || findActiveRun(entry, scopeKey)) {
		return;
	}
	entry.pendingStarts.add(scopeKey);
	publish(agentId, entry);
	Util.CommitMutation(
		StartAgenticWorkerRunMutation,
		{agentId, ...toScopeInput(scope)},
		(response: StartAgenticWorkerRunMutation$data) => {
			const node = response?.startAgenticWorkerRun?.run;
			finishStart(agentId, scopeKey, node ? toAgentRun(node) : null);
		},
		true,
		null,
		(error: Error, showStandardError?: StandardErrorFlow) => {
			if (showStandardError?.(error) === true) {
				return;
			}
			finishStart(agentId, scopeKey, null);
		}
	);
};

export const stopAgentRun = (agentId: string, scope: AgentRunScope, onStopped: StopListener): void => {
	const entry = entryFor(agentId);
	const scopeKey = toScopeKey(scope);
	if (entry.restartsAfterStop.has(scopeKey)) {
		entry.restartsAfterStop.delete(scopeKey);
		publish(agentId, entry);
		return;
	}
	if (entry.pendingStarts.has(scopeKey)) {
		entry.heldStops.set(scopeKey, onStopped);
		publish(agentId, entry);
		return;
	}
	const run = findActiveRun(entry, scopeKey);
	if (run && !entry.stopsInFlight.has(run.id)) {
		sendStop(agentId, run, onStopped);
	}
};

export const isScopeWorking = (snapshot: AgentRunSnapshot, scope: AgentRunScope): boolean => {
	const scopeKey = toScopeKey(scope);
	if (snapshot.restartAfterStopKeys.has(scopeKey)) {
		return true;
	}
	if (snapshot.pendingStartKeys.has(scopeKey)) {
		return !snapshot.heldStopKeys.has(scopeKey);
	}
	return snapshot.activeRuns.some(run => toScopeKey(run.scope) === scopeKey && !snapshot.stoppingRunIds.has(run.id));
};

const coversRow =
	(row: AgentRow) =>
	(candidate: AgentRow): boolean =>
		candidate.rowType === row.rowType && candidate.rowId === row.rowId;

export const getRowState = (snapshot: AgentRunSnapshot, row: AgentRow): AgentRowState => {
	if (isScopeWorking(snapshot, toRowScope(row))) {
		return {
			status: AGENT_ROW_STATUS.WORKING,
			result: null,
		};
	}
	const result = snapshot.waitingResults.find(waiting => waiting.rows.some(coversRow(row))) ?? null;
	return {
		status: result ? AGENT_ROW_STATUS.READY : AGENT_ROW_STATUS.IDLE,
		result,
	};
};

export const countWaitingResults = (snapshot: AgentRunSnapshot): number =>
	snapshot.waitingResults.filter(result => !result.hidden).length;

export const clearAgentRunStore = (): void => {
	entries.forEach(entry => {
		cancelPoll(entry);
		entry.listeners.clear();
	});
	entries.clear();
};

type AgentRunCommands = {
	startRun: () => void;
	stopRun: () => void;
};

export type AgentRowControls = AgentRowState & AgentRunCommands;

type AgentPageState = {
	isRunning: boolean;
	waitingCount: number;
};

export type AgentPageControls = AgentPageState & AgentRunCommands;

const useAgentRunCommands = (agentId: string, scope: AgentRunScope): AgentRunCommands => {
	const {formatMessage} = useIntl();
	return {
		startRun: () => startAgentRun(agentId, scope),
		stopRun: () =>
			stopAgentRun(agentId, scope, () =>
				createToast({
					duration: RUN_STOPPED_TOAST_DURATION_MILLISECONDS,
					message: formatMessage({id: AGENTIC_WORKERS_MESSAGE.TOAST_RUN_STOPPED}),
				})
			),
	};
};

// Selects from the latest snapshot as it renders, and re-renders only when a store change changes the selection, so a
// change to one row leaves every other row's control alone.
const useAgentRunSelection = <Selection>(
	agentId: string,
	select: (snapshot: AgentRunSnapshot) => Selection,
	isSameSelection: (rendered: Selection, latest: Selection) => boolean
): Selection => {
	const selection = select(getAgentRunSnapshot(agentId));
	const [, rerender] = useReducer((renders: number) => renders + 1, 0);
	const renderedRef = useRef({selection, select, isSameSelection});

	// Updated as the render commits, so a store change that lands before the effects run is compared with what shows.
	useLayoutEffect(() => {
		renderedRef.current = {selection, select, isSameSelection};
	});

	useEffect(() => {
		const rerenderOnChange = (): void => {
			const rendered = renderedRef.current;
			if (!rendered.isSameSelection(rendered.selection, rendered.select(getAgentRunSnapshot(agentId)))) {
				rerender();
			}
		};
		rerenderOnChange();
		return subscribeToAgentRun(agentId, rerenderOnChange);
	}, [agentId]);

	return selection;
};

const isSameRowState = (rendered: AgentRowState, latest: AgentRowState): boolean =>
	rendered.status === latest.status && isEqual(rendered.result, latest.result);

const getPageState = (snapshot: AgentRunSnapshot): AgentPageState => ({
	isRunning: isScopeWorking(snapshot, PAGE_RUN_SCOPE),
	waitingCount: countWaitingResults(snapshot),
});

export const useAgentRowState = (declaration: AgentDeclaration, row: AgentRow): AgentRowControls => {
	const rowState = useAgentRunSelection(declaration.id, snapshot => getRowState(snapshot, row), isSameRowState);
	const commands = useAgentRunCommands(declaration.id, toRowScope(row));
	return {
		...rowState,
		...commands,
	};
};

export const useAgentPageState = (declaration: AgentDeclaration): AgentPageControls => {
	const pageState = useAgentRunSelection(declaration.id, getPageState, isEqual);
	const commands = useAgentRunCommands(declaration.id, PAGE_RUN_SCOPE);
	return {
		...pageState,
		...commands,
	};
};
